import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Users, Building2, GraduationCap, Shield, TrendingUp, Heart, ArrowRight } from 'lucide-react';
import { FadeIn } from '@/components/Animations';
import AnimatedCounter from '@/components/AnimatedCounter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const defaultMetrics = [
  { id: '1', label: 'Riders Supported', value: 500, icon: 'Users', order: 0 },
  { id: '2', label: 'Corporate Partners', value: 15, icon: 'Building2', order: 1 },
  { id: '3', label: 'Training Hours Delivered', value: 2500, icon: 'GraduationCap', order: 2 },
  { id: '4', label: 'Safety Campaigns', value: 12, icon: 'Shield', order: 3 },
  { id: '5', label: 'Communities Reached', value: 25, icon: 'Heart', order: 4 },
  { id: '6', label: 'Growth Rate %', value: 45, icon: 'TrendingUp', order: 5 },
];

const iconMap = {
  Users,
  Building2,
  GraduationCap,
  Shield,
  Heart,
  TrendingUp,
};

const impactStories = [
  {
    title: 'Rider Safety Training',
    description: 'Comprehensive road safety programs have reduced accident rates among our riders by 40%.',
    stat: '40%',
    statLabel: 'Accident reduction',
  },
  {
    title: 'Economic Empowerment',
    description: 'Riders in our program report an average 30% increase in monthly earnings.',
    stat: '30%',
    statLabel: 'Income increase',
  },
  {
    title: 'Community Building',
    description: 'Our support network has helped riders build lasting connections and mentorship relationships.',
    stat: '95%',
    statLabel: 'Rider satisfaction',
  },
];

const ImpactPage = () => {
  const [metrics, setMetrics] = useState(defaultMetrics);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await fetch(`${API_URL}/api/impact`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setMetrics(data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch metrics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, []);

  return (
    <div className="min-h-screen pt-20" data-testid="impact-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-emerald-50 via-slate-50 to-white">
        <div className="container-wide">
          <FadeIn>
            <div className="max-w-4xl">
              <span className="inline-block px-4 py-2 bg-emerald-100 text-emerald-600 rounded-full text-sm font-semibold tracking-wide uppercase mb-6">
                Our Impact
              </span>
              <h1 className="text-4xl md:text-6xl font-heading font-bold text-slate-900 mb-6">
                Making a Real <span className="text-primary-mlm">Difference</span>
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed">
                Every number represents a life impacted, a family supported, and a step toward 
                a more equitable gig economy. Here's what we've achieved together.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Impact Metrics Grid */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container-wide">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {metrics.map((metric, index) => {
              const IconComponent = iconMap[metric.icon] || Users;
              const isHighlighted = index === 0;
              
              return (
                <FadeIn key={metric.id} delay={0.1 * index}>
                  <div className={`rounded-2xl p-8 text-center ${
                    isHighlighted ? 'bg-safety-yellow' : 'bg-slate-50'
                  } hover:shadow-lg transition-all duration-300`}>
                    <IconComponent className={`w-12 h-12 mx-auto mb-4 ${
                      isHighlighted ? 'text-slate-900' : 'text-primary-mlm'
                    }`} />
                    <p className={`text-5xl md:text-6xl font-heading font-bold mb-2 ${
                      isHighlighted ? 'text-slate-900' : 'text-slate-900'
                    }`}>
                      <AnimatedCounter 
                        end={metric.value} 
                        suffix={metric.label.includes('%') ? '%' : '+'} 
                      />
                    </p>
                    <p className={`text-sm font-medium ${
                      isHighlighted ? 'text-slate-700' : 'text-slate-600'
                    }`}>
                      {metric.label}
                    </p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Impact Stories */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container-wide">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-slate-900 mb-6">
                Impact Stories
              </h2>
              <p className="text-lg text-slate-600">
                Behind the numbers are real stories of transformation and growth.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8">
            {impactStories.map((story, index) => (
              <FadeIn key={story.title} delay={0.1 * index}>
                <div className="bg-white rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all duration-300 h-full">
                  <div className="text-4xl font-heading font-bold text-primary-mlm mb-2">
                    {story.stat}
                  </div>
                  <p className="text-sm text-slate-500 mb-4">{story.statLabel}</p>
                  <h3 className="text-xl font-heading font-semibold text-slate-900 mb-3">
                    {story.title}
                  </h3>
                  <p className="text-slate-600">{story.description}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Future Goals */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-primary-mlm to-purple-700">
        <div className="container-wide">
          <FadeIn>
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
                Our Goals for 2025
              </h2>
              <p className="text-xl text-purple-100 leading-relaxed mb-12">
                We're not stopping here. With your support, we aim to reach even more riders 
                and create lasting change across South Africa.
              </p>
              <div className="grid md:grid-cols-4 gap-8">
                {[
                  { value: '2,000', label: 'Riders Supported' },
                  { value: '50', label: 'Corporate Partners' },
                  { value: '10,000', label: 'Training Hours' },
                  { value: '100', label: 'Communities' },
                ].map((goal) => (
                  <div key={goal.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                    <p className="text-3xl md:text-4xl font-heading font-bold text-safety-yellow mb-2">
                      {goal.value}
                    </p>
                    <p className="text-purple-200 text-sm">{goal.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container-wide">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-6">
                Help Us Grow Our Impact
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Every partnership, every contribution, every voice amplifying our message 
                helps us reach more riders and create more change.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  asChild
                  className="bg-primary-mlm hover:bg-[#5b21b6] text-white rounded-full px-8 py-6 font-semibold"
                  data-testid="impact-partner-btn"
                >
                  <Link to="/corporate-csi">
                    Become a Partner
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-2 border-primary-mlm text-primary-mlm hover:bg-primary-mlm hover:text-white rounded-full px-8 py-6 font-semibold"
                  data-testid="impact-contact-btn"
                >
                  <Link to="/contact">
                    Contact Us
                  </Link>
                </Button>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default ImpactPage;
